@if (roomData$ | async; as roomData) {
	<div data-test-id="room-detail">
		@if (roomData.hasImages) {
			<section
				class="section-spacing-y bg-section-gradient-67p-inverted md:section-spacing-x max-md:pt-0"
			>
				<div class="section-inner 2xl:section-inner-loose">
					<vp-image-grid [images]="roomData.images" class="block" />
				</div>
			</section>
		}
		<section
			[class]="cn(roomData.hasImages && 'pt-0')"
			class="bg-white section-spacing"
		>
			<div class="section-inner section-gap flex-col">
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						<div>
							<header>
								<h1
									class="heading-group heading-group-lg"
									data-test-id="room-detail-title"
								>
									{{ roomData.title }}
								</h1>
								<p
									class="heading-group-overline"
									role="doc-subtitle"
								>
									{{ roomData.category }}
								</p>
							</header>
							<vp-icon-list
								[items]="roomFacilityIconList"
								data-test-id="room-detail-facilities"
							/>
						</div>
						<div class="flex flex-row gap-3">
							<vp-tag
								color="accent"
								data-test-id="room-detail-checkin"
							>
								{{
									'global.checkin-time'
										| translate
											: {
													time: roomData.checkInTime,
											  }
								}}
							</vp-tag>
							<vp-tag
								color="accent"
								data-test-id="room-detail-checkout"
							>
								{{
									'global.checkout-time'
										| translate
											: {
													time: roomData.checkOutTime,
											  }
								}}
							</vp-tag>
						</div>
						<vp-read-more>
							<markdown
								[data]="roomData.description"
								class="markdown"
								data-test-id="room-detail-description"
							/>
						</vp-read-more>
						<div
							class="list-checkmarks flex list-grid w-full flex-col gap-4 md:gap-8"
						>
							<h2 class="running-head mb-0">
								{{ 'global.room' | translate }}
								{{ 'global.facilities' | translate }}
							</h2>
							<vp-list-grid
								[listItems]="roomFacilities"
								[name]="'room-facilities'"
								[sheetTitle]="
									('global.room' | translate) +
									' ' +
									('global.facilities' | translate)
								"
								[useCol2]="true"
							/>
						</div>
					</div>
					<div class="flex-col lg:w-1/3">
						@if (roomData.isBookable) {
							<sb-availability-widget
								[currency]="hotelStore.currency()"
								[hotelGuid]="roomData.hotel.guid"
								[hotelSlug]="this.hotelSlug"
								[language]="this.currentLanguage()"
								[occupancyRestrictions]="
									roomData.occupancyRestrictions
								"
								[roomGuid]="roomData.guid"
								[roomSlug]="roomSlug"
							>
								@if (roomData.price?.amount > 0) {
									<vp-pricing
										[conditions]="
											roomData.priceConditionKey
												| translate
										"
										[currency]="
											roomData.price.currency || 'EUR'
										"
										[indication]="true"
										[price]="roomData.displayPrice"
										[pricingToolTip]="{
											dateRange: roomData.price.dateRange,
											items: [
												{
													name: roomData.title,
													price: roomData.price
														?.amountExcludingCharges,
												},
											],
											occupancy:
												roomData.pricingToolTipOccupancy,
											taxes: {
												tourist: roomData.price.charges,
											},
											total: roomData.price.amount,
										}"
										[requestPrice]="
											roomData.price.onRequest
										"
										alignment="self-start"
										data-test-id="room-detail-price"
										size="lg"
									/>
								}
							</sb-availability-widget>
						}
						@if (roomData.hotel?.rating?.score) {
							<vp-rating
								[isHorizontal]="true"
								[numberOfRatings]="
									roomData.hotel.rating.numberOfRatings
								"
								[score]="roomData.hotel.rating.score"
							/>
						}
					</div>
				</div>
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						@if (roomData.hotel) {
							<h2
								class="running-head running-head-center"
								data-test-class="hotel-header"
							>
								{{
									'storyblok.pages.hotel-information'
										| translate
								}}
							</h2>
							@if (roomData.hotel.images?.length > 0) {
								<vp-mini-carousel
									[items]="hotelImages"
									aspectRatio="21:9"
								/>
							}
							<vp-tab-menu
								color="white"
								data-test-id="room-hotel-info"
							>
								<vp-tab-menu-item
									[content]="facilitiesRef"
									[label]="'global.facilities' | translate"
									name="facilities"
								/>
								<vp-tab-menu-item
									[content]="goodToKnowsRef"
									[label]="'global.good-to-know' | translate"
									name="good-to-knows"
								/>
								@if (
									this.hotelStore.address().latitude &&
									this.hotelStore.address().longitude
								) {
									<vp-tab-menu-item
										[content]="mapRef"
										[label]="'global.map' | translate"
										name="map"
									/>
								}
							</vp-tab-menu>
							<ng-template #facilitiesRef>
								<vp-list-grid
									[listItems]="
										roomData.hotel?.facilities || []
									"
									[name]="'hotel-facilities'"
									[sheetTitle]="
										('global.hotel.label' | translate) +
										' ' +
										('global.facilities' | translate)
									"
									[useCol2]="true"
								/>
							</ng-template>
							<ng-template #goodToKnowsRef>
								<vp-list-grid
									[listItems]="
										roomData.hotel?.goodToKnows || []
									"
									[name]="'hotel-good-to-knows'"
									[sheetTitle]="
										'global.good-to-know' | translate
									"
									[useCol2]="false"
								/>
							</ng-template>
							<ng-template #mapRef>
								<vp-map
									[lat]="this.hotelStore.address().latitude"
									[long]="this.hotelStore.address().longitude"
								/>
							</ng-template>
						}
					</div>
					<div class="lg:w-1/3"></div>
				</div>
			</div>
		</section>
	</div>
}
